/**
 * CommodityLegend Component
 * Dynamic legend that shows commodities present in mine results
 * Display-only component with alphabetical sorting
 */

import { buildLegendData } from '../utils/commodityUtils.js';

export class CommodityLegend {
  constructor(containerOrId) {
    // Handle both element and ID inputs
    if (typeof containerOrId === 'string') {
      this.containerId = containerOrId;
      this.container = null;
    } else {
      this.container = containerOrId;
      this.containerId = containerOrId?.id || 'unknown';
    }
    this.legendData = null;
    this.isVisible = true;
    this.mineData = [];

    // Bind methods to preserve context
    this.render = this.render.bind(this);
    this.updateMineData = this.updateMineData.bind(this);

    this.init();
  }

  /**
   * Initialize the legend component
   */
  init() {
    // Get container if not already set
    if (!this.container) {
      this.container = document.getElementById(this.containerId);
    }

    if (!this.container) {
      console.error(`CommodityLegend: Container with id '${this.containerId}' not found`);
      return;
    }

    // Initial render with empty state
    this.updateLegendData();
    this.render();

    console.log('CommodityLegend: Initialized successfully');
  }


  /**
   * Update mine data and refresh legend
   * @param {Array} newMineData - Fresh mine data from API
   */
  updateMineData(newMineData) {
    if (!Array.isArray(newMineData)) {
      console.warn('CommodityLegend: Invalid mine data provided, using empty array');
      newMineData = [];
    }

    this.mineData = newMineData;
    this.updateLegendData();
    this.render();

    console.log('CommodityLegend: Updated with', newMineData.length, 'mines');
  }

  /**
   * Update legend data based on current mine data
   */
  updateLegendData() {
    this.legendData = buildLegendData(this.mineData);
  }


  /**
   * Toggle legend visibility
   */
  toggleVisibility() {
    this.isVisible = !this.isVisible;
    this.render();
  }

  /**
   * Set up event delegation for legend interactions
   */
  setupEventListeners() {
    this.container.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;

      if (action === 'toggle') {
        this.toggleVisibility();
      }
    });
  }

  /**
   * Render the complete legend component
   */
  render() {
    if (!this.container || !this.legendData) {
      return;
    }

    const { visibleCommodities, totalCommodities, criticalCount } = this.legendData;

    this.container.innerHTML = `
      <div class="commodity-legend ${this.isVisible ? 'visible' : 'hidden'}">
        <div class="legend-header">
          <h3>
            <span class="legend-title">Minerals</span>
            <button class="legend-toggle" data-action="toggle" aria-label="Toggle legend visibility">
              <span class="toggle-icon">${this.isVisible ? '−' : '+'}</span>
            </button>
          </h3>
          <div class="legend-summary">
            <span class="commodity-count">${totalCommodities} found</span>
            ${criticalCount > 0 ? `<span class="critical-count">${criticalCount} critical</span>` : ''}
          </div>
        </div>

        ${this.isVisible ? this.renderLegendContent() : ''}
      </div>
    `;

    // Set up event delegation (after initial render)
    this.setupEventListeners();

  }

  /**
   * Render legend content (items only)
   */
  renderLegendContent() {
    const { visibleCommodities } = this.legendData;

    if (visibleCommodities.length === 0) {
      return this.renderEmptyState();
    }

    return `
      <div class="legend-items" role="list" aria-label="Commodity legend items">
        ${visibleCommodities.map(commodity => this.renderLegendItem(commodity)).join('')}
      </div>
    `;
  }

  /**
   * Render individual legend item
   * @param {Object} commodity - Commodity data object
   */
  renderLegendItem(commodity) {
    const {
      name,
      displayName,
      color,
      isCritical,
      count
    } = commodity;

    return `
      <div class="legend-item ${isCritical ? 'critical' : ''}"
           role="listitem"
           aria-label="${displayName} (${count} mines)">

        <div class="legend-item-color" style="background-color: ${color};" aria-hidden="true"></div>

        <div class="legend-item-content">
          <div class="legend-item-header">
            <span class="legend-item-name">${displayName}</span>
            ${isCritical ? '<span class="critical-badge" title="Critical Mineral">★</span>' : ''}
          </div>
          <div class="legend-item-count">${count} mine${count !== 1 ? 's' : ''}</div>
        </div>
      </div>
    `;
  }

  /**
   * Render empty state when no commodities found
   */
  renderEmptyState() {
    return `
      <div class="legend-empty-state">
        <div class="empty-icon">⚡</div>
        <div class="empty-message">No commodities found</div>
        <div class="empty-hint">Try adjusting your filters or changing the map view</div>
      </div>
    `;
  }

  /**
   * Get current legend statistics
   * @returns {Object} Statistics about current legend state
   */
  getStatistics() {
    if (!this.legendData) {
      return { totalCommodities: 0, criticalCount: 0 };
    }

    return {
      totalCommodities: this.legendData.totalCommodities,
      criticalCount: this.legendData.criticalCount,
      totalMines: this.mineData.length
    };
  }

  /**
   * Cleanup method to remove event listeners
   */
  destroy() {
    console.log('CommodityLegend: Destroyed successfully');
  }
}

// Export for module usage
export default CommodityLegend;